import { ArrowUpRightIcon, SettingsIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

import { NotificationFilterButton } from "./NotificationFilterButton";
import { NotificationInboxFilterToggle } from "./NotificationInboxFilterToggle";
import { NotificationsTable } from "./NotificationsTable";
import { NotificationTopCandidatesSections } from "./NotificationTopCandidatesSections";
import { Column, Row } from "@/components";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useSidebar } from "@/components/ui/sidebar";

import { useGetNotifications } from "@/hooks/queries/useGetNotifications";
import { useUpdateSetNotifications } from "@/hooks/updateSets/useUpdateSetNotifications";
import { unreadNotificationsCountQueryKey } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import {
  MIXPANEL_NOTIFICATION_SURFACES,
  MIXPANEL_SCREENS,
} from "@/lib/mixpanel/types";
import { filterNotifications } from "@/lib/notificationFilters";
import { cn } from "@/lib/utils";
import { NotificationFilterState, NotificationInboxFilter } from "@/types";

// Opens showing unread only, unlike the full page which defaults to all.
const slideoutDefaultFilterState: NotificationFilterState = {
  categoryIds: [],
  inboxFilter: NotificationInboxFilter.UNREAD,
  searchQuery: "",
  types: [],
};

export const NotificationsSlideout = ({
  initialFilterState,
  open,
  setOpen,
}: {
  initialFilterState?: NotificationFilterState;
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  /* React Hooks */
  const { open: isSidebarOpen } = useSidebar();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      const isInsidePanel = panelRef.current?.contains(target);
      const isInsideSidebar = target.closest("[data-sidebar]") !== null;
      // Dropdowns (category filter) portal to the body — a click in
      // one is not "outside" even though it's not inside the panel node.
      const isInsidePortal =
        target.closest("[data-radix-popper-content-wrapper]") !== null;

      if (isInsidePanel || isInsideSidebar || isInsidePortal) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open, setOpen]);

  /* Render */
  const render = () => (
    <Column
      ref={panelRef}
      className={cn(
        // Offsets mirror SIDEBAR_WIDTH / SIDEBAR_WIDTH_ICON in ui/sidebar.tsx
        // so the panel sits flush against the sidebar.
        "fixed inset-y-0 z-[35] w-[400px] border-r border-slate-200 bg-white transition-[left,transform] duration-300 ease-in-out",
        isSidebarOpen ? "left-[14.75rem]" : "left-[3rem]",
        open
          ? "translate-x-0 shadow-lg"
          : "pointer-events-none -translate-x-full",
      )}
    >
      <SlideoutContent
        initialFilterState={initialFilterState}
        open={open}
        setOpen={setOpen}
      />
    </Column>
  );

  return render();
};

const SlideoutContent = ({
  initialFilterState,
  open,
  setOpen,
}: {
  initialFilterState?: NotificationFilterState;
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const navigate = useNavigate();

  /* Database CRUD Operations */
  // Read
  const {
    data: notifications = [],
    isPending,
    isError,
    refetch,
  } = useGetNotifications();
  const { mutate: updateNotifications } = useUpdateSetNotifications();

  /* React Hooks */
  const [filterState, setFilterState] = useState<NotificationFilterState>(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    initialFilterState || slideoutDefaultFilterState,
  );
  const queryClient = useQueryClient();
  const hasDismissedOnOpenRef = useRef(false);

  const filteredNotifications = filterNotifications(notifications, filterState);

  const isSectioned =
    filterState.inboxFilter === NotificationInboxFilter.UNREAD &&
    filteredNotifications.length > 0;
  const unreadIds = notifications
    .filter((notification) => notification.readAt === null)
    .map((notification) => notification.id);

  // Reset every time the panel opens — to the unread view, or to whatever scope
  // the surface that opened it asked for.
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      setFilterState(initialFilterState || slideoutDefaultFilterState);
    }
  }, [initialFilterState, open]);

  useEffect(() => {
    if (!open) {
      hasDismissedOnOpenRef.current = false;
      return;
    }
    if (hasDismissedOnOpenRef.current) return;
    hasDismissedOnOpenRef.current = true;

    void queryClient.invalidateQueries({
      queryKey: unreadNotificationsCountQueryKey,
    });
    void refetch().then(({ data }) => {
      const undismissedIds = (data ?? [])
        .filter(
          (notification) =>
            notification.readAt === null && notification.dismissedAt === null,
        )
        .map((notification) => notification.id);

      if (undismissedIds.length > 0) {
        updateNotifications({ notificationIds: undismissedIds, dismiss: true });
      }
    });
  }, [open, queryClient, refetch, updateNotifications]);

  /* Handlers */
  const handleMarkAllAsRead = () => {
    mixpanelUtils.trackButtonClick(
      "Mark all notifications as read",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      { surface: MIXPANEL_NOTIFICATION_SURFACES.SLIDEOUT },
    );
    updateNotifications({ notificationIds: unreadIds, markAsRead: true });
  };

  const handleOpenFullPage = () => {
    mixpanelUtils.trackButtonClick(
      "Open notifications page",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      { surface: MIXPANEL_NOTIFICATION_SURFACES.SLIDEOUT },
    );
    setOpen(false);
    void navigate("/notifications");
  };

  const handleOpenSettings = () => {
    mixpanelUtils.trackButtonClick(
      "Notification settings",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      { surface: MIXPANEL_NOTIFICATION_SURFACES.SLIDEOUT },
    );
    setOpen(false);
    void navigate("/notifications/settings");
  };

  /* Render */
  const renderList = () => {
    if (isSectioned) {
      return (
        <NotificationTopCandidatesSections
          hasAnyNotifications={notifications.length > 0}
          isError={isError}
          isPending={isPending}
          notifications={filteredNotifications}
          trackingSurface={MIXPANEL_NOTIFICATION_SURFACES.SLIDEOUT}
        />
      );
    }
    return (
      <NotificationsTable
        hasAnyNotifications={notifications.length > 0}
        isCompact
        isError={isError}
        isPending={isPending}
        notifications={filteredNotifications}
        trackingSurface={MIXPANEL_NOTIFICATION_SURFACES.SLIDEOUT}
      />
    );
  };

  const render = () => (
    <>
      <Column className="gap-3 border-b border-slate-100 px-4 pb-3 pt-4">
        <Row className="items-center justify-between gap-2">
          <Label className="text-base font-semibold text-slate-900">
            Notifications
          </Label>
          <Row className="items-center gap-1">
            <NotificationInboxFilterToggle
              filterState={filterState}
              isCompact
              notifications={notifications}
              setFilterState={setFilterState}
              trackingSurface={MIXPANEL_NOTIFICATION_SURFACES.SLIDEOUT}
            />
            <Button
              className="h-7 w-7"
              onClick={() => {
                setOpen(false);
              }}
              size="icon"
              variant="ghost"
            >
              <XIcon className="h-3.5 w-3.5 text-slate-500" />
            </Button>
          </Row>
        </Row>
        <Row className="items-center justify-between gap-2">
          <Row className="items-center gap-2">
            <NotificationFilterButton
              filterState={filterState}
              setFilterState={setFilterState}
              trackingSurface={MIXPANEL_NOTIFICATION_SURFACES.SLIDEOUT}
            />
            <Button
              aria-label="Notification settings"
              className="h-9 w-9 shrink-0 rounded-full border-slate-200 bg-white p-0 text-slate-700 shadow-none hover:bg-slate-50"
              onClick={handleOpenSettings}
              size="icon"
              variant="outline"
            >
              <SettingsIcon className="h-4 w-4" />
            </Button>
          </Row>
          <Button
            className="h-7 px-2 text-xs font-medium text-slate-600"
            disabled={unreadIds.length === 0}
            onClick={handleMarkAllAsRead}
            size="sm"
            variant="ghost"
          >
            Mark all as read
          </Button>
        </Row>
      </Column>
      <Column className="min-h-0 flex-1 overflow-y-auto px-4">
        {renderList()}
        {/* Sits right below a short list; sticks to the bottom once the list
            is long enough to scroll. */}
        <Row className="sticky bottom-0 -mx-4 justify-end border-t border-slate-100 bg-white px-4 py-4">
          <Button
            className="h-7 gap-1 border-slate-200 px-2 text-sm font-medium text-primary-700 hover:text-primary-700"
            onClick={handleOpenFullPage}
            size="sm"
            variant="outline"
          >
            View all
            <ArrowUpRightIcon className="h-3.5 w-3.5" />
          </Button>
        </Row>
      </Column>
    </>
  );

  return render();
};
